import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { List, FolderTree, Loader2, Lock, Unlock, ChevronDown, ChevronRight, Pencil, Trash2, X, Save } from 'lucide-react';
import { authHeaders } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRUPO_LABELS = {
  receita_producao: 'Receita Produção', aporte_capital: 'Aporte Capital', venda_ativo: 'Venda Ativo',
  combustivel_maquinas: 'Combustível Máquinas', combustivel_veiculos: 'Combustível Veículos',
  lubrificacao: 'Lubrificação', insumos: 'Insumos', pecas: 'Peças', material_rodante: 'Material Rodante',
  manutencao_maquinas: 'Manutenção Máquinas', manutencao_veiculos: 'Manutenção Veículos',
  pessoal: 'Pessoal', deslocamento: 'Deslocamento', campo_operacao: 'Campo/Operação',
  saude: 'Saúde', administrativo: 'Administrativo', controle_interno: 'Controle Interno', residual: 'Residual',
};

const GRUPO_ORDER = Object.keys(GRUPO_LABELS);

export default function FinanceiroPlanoContas() {
  const { user } = useAuth();
  const isMaster = user && ['master', 'master_auxiliar'].includes((user.role || '').toLowerCase());
  const canEdit = user && ['master', 'master_auxiliar', 'admin', 'gestor'].includes((user.role || '').toLowerCase());

  const [loading, setLoading] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [openGroups, setOpenGroups] = useState(new Set(GRUPO_ORDER));
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ nome: '', tipo: 'saida', grupo: 'residual', requer_destino: false, requer_responsavel: false });
  const [editCat, setEditCat] = useState(null);  // categoria em edição inline
  const [editForm, setEditForm] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/financeiro/plano-de-contas`, { headers: authHeaders() });
      if (r.ok) setCategorias(await r.json());
    } catch { toast.error('Erro ao carregar plano de contas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const grouped = {};
  categorias.forEach(c => {
    if (!grouped[c.grupo]) grouped[c.grupo] = [];
    grouped[c.grupo].push(c);
  });

  const toggleGroup = (g) => {
    setOpenGroups(prev => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      return next;
    });
  };

  const handleToggle = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/financeiro/plano-de-contas/${id}/toggle`, { method: 'PATCH', headers: authHeaders() });
      if (r.ok) { toast.success('Status atualizado'); fetchData(); }
      else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } catch { toast.error('Erro de conexão'); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { toast.error('Nome é obrigatório'); return; }
    try {
      const r = await fetch(`${API_URL}/api/financeiro/plano-de-contas`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form),
      });
      if (r.ok) { toast.success('Categoria criada'); setShowForm(false); setForm({ nome: '', tipo: 'saida', grupo: 'residual', requer_destino: false, requer_responsavel: false }); fetchData(); }
      else { const d = await r.json(); toast.error(d.detail || 'Erro'); }
    } catch { toast.error('Erro de conexão'); }
  };

  const openEdit = (cat) => {
    if (cat.sistema && !isMaster) {
      toast.error('Categorias do sistema só podem ser editadas pelo Master.');
      return;
    }
    setEditCat(cat);
    setEditForm({
      nome: cat.nome, tipo: cat.tipo, grupo: cat.grupo,
      requer_destino: !!cat.requer_destino, requer_responsavel: !!cat.requer_responsavel,
      destino_multiplo: !!cat.destino_multiplo, exibe_alerta: !!cat.exibe_alerta,
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.nome?.trim()) { toast.error('Nome é obrigatório'); return; }
    try {
      const r = await fetch(`${API_URL}/api/financeiro/plano-de-contas/${editCat.id}`, {
        method: 'PATCH', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(editForm),
      });
      if (r.ok) {
        toast.success('Categoria atualizada');
        setEditCat(null);
        fetchData();
      } else {
        const d = await r.json();
        toast.error(d.detail || 'Erro ao atualizar');
      }
    } catch { toast.error('Erro de conexão'); }
  };

  const handleDelete = async (cat) => {
    if (cat.sistema && !isMaster) {
      toast.error('Categorias do sistema só podem ser excluídas pelo Master.');
      return;
    }
    try {
      // Preview: quantos lançamentos ativos
      const rc = await fetch(`${API_URL}/api/financeiro/plano-de-contas/${cat.id}/lancamentos-count`,
                              { headers: authHeaders() });
      const c = await rc.json();
      const n = c.lancamentos_ativos || 0;
      const aviso = n > 0
        ? `⚠ ATENÇÃO: ${n} lançamento(s) ATIVO(S) usam esta categoria.\n\n` +
          `Os lançamentos NÃO serão excluídos — apenas a categoria ficará inativa ` +
          `e marcada como excluída. Você precisará reclassificá-los manualmente.\n\n` +
          `Deseja prosseguir mesmo assim?`
        : `Confirmar exclusão da categoria "${cat.nome}"?`;
      if (!window.confirm(aviso)) return;
      let motivo = '';
      if (n > 0) {
        motivo = window.prompt('Motivo da exclusão (opcional, registrado em auditoria):', '') || '';
      }
      const qs = new URLSearchParams({ force: String(n > 0), ...(motivo ? { motivo } : {}) }).toString();
      const r = await fetch(`${API_URL}/api/financeiro/plano-de-contas/${cat.id}?${qs}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Categoria excluída. ${d.lancamentos_preservados > 0 ? `${d.lancamentos_preservados} lançamento(s) preservado(s).` : ''}`);
        fetchData();
      } else {
        const d = await r.json();
        const msg = typeof d.detail === 'string' ? d.detail : (d.detail?.message || 'Erro ao excluir');
        toast.error(msg);
      }
    } catch { toast.error('Erro de conexão'); }
  };

  return (
    <div className="space-y-6" data-testid="financeiro-plano-contas">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Plano de Contas</h1>
          <p className="text-sm text-gray-500">{categorias.length} categorias em {Object.keys(grouped).length} grupos</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="btn-nova-categoria"
          className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 transition-colors text-sm">
          + Nova Categoria
        </button>
      </div>

      {/* Form nova categoria */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-[#111] border border-gray-800 rounded-xl p-5 space-y-4" data-testid="form-nova-categoria">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Nome</label>
              <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none" placeholder="Nome da categoria" />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Tipo</label>
              <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none">
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Grupo</label>
              <select value={form.grupo} onChange={e => setForm(f => ({ ...f, grupo: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none">
                {GRUPO_ORDER.map(g => <option key={g} value={g}>{GRUPO_LABELS[g]}</option>)}
              </select>
            </div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
              <input type="checkbox" checked={form.requer_destino} onChange={e => setForm(f => ({ ...f, requer_destino: e.target.checked }))} className="accent-yellow-500" />
              Requer destino
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
              <input type="checkbox" checked={form.requer_responsavel} onChange={e => setForm(f => ({ ...f, requer_responsavel: e.target.checked }))} className="accent-yellow-500" />
              Requer responsável
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-yellow-500 text-black font-semibold rounded-lg hover:bg-yellow-400 text-sm" data-testid="btn-salvar-categoria">Salvar</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 text-sm">Cancelar</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-yellow-500" size={32} /></div>
      ) : (
        <div className="space-y-2">
          {GRUPO_ORDER.filter(g => grouped[g]).map(g => (
            <div key={g} className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
              <button onClick={() => toggleGroup(g)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-800/50 transition-colors text-left">
                {openGroups.has(g) ? <ChevronDown size={16} className="text-yellow-500" /> : <ChevronRight size={16} className="text-gray-500" />}
                <FolderTree size={16} className="text-yellow-500" />
                <span className="text-sm font-semibold text-white flex-1">{GRUPO_LABELS[g] || g}</span>
                <span className="text-xs text-gray-500">{grouped[g].length} categorias</span>
              </button>
              {openGroups.has(g) && (
                <div className="border-t border-gray-800">
                  {grouped[g].map(cat => {
                    const isEditing = editCat?.id === cat.id;
                    if (isEditing) {
                      return (
                        <div key={cat.id} className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-gray-800/50 bg-yellow-500/5">
                          <input type="text" value={editForm.nome}
                            onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                            data-testid={`edit-nome-${cat.id}`}
                            className="flex-1 min-w-[200px] bg-[#0A0A0A] border border-yellow-500/30 rounded px-2 py-1 text-white text-sm" />
                          <select value={editForm.tipo}
                            onChange={(e) => setEditForm({ ...editForm, tipo: e.target.value })}
                            data-testid={`edit-tipo-${cat.id}`}
                            className="bg-[#0A0A0A] border border-gray-800 rounded px-2 py-1 text-white text-xs">
                            <option value="saida">Saída</option>
                            <option value="entrada">Entrada</option>
                          </select>
                          <select value={editForm.grupo}
                            onChange={(e) => setEditForm({ ...editForm, grupo: e.target.value })}
                            data-testid={`edit-grupo-${cat.id}`}
                            className="bg-[#0A0A0A] border border-gray-800 rounded px-2 py-1 text-white text-xs">
                            {GRUPO_ORDER.map(go => <option key={go} value={go}>{GRUPO_LABELS[go]}</option>)}
                          </select>
                          <label className="flex items-center gap-1 text-xs text-gray-300">
                            <input type="checkbox" checked={editForm.requer_destino || false}
                              onChange={(e) => setEditForm({ ...editForm, requer_destino: e.target.checked })} />
                            Destino
                          </label>
                          <label className="flex items-center gap-1 text-xs text-gray-300">
                            <input type="checkbox" checked={editForm.requer_responsavel || false}
                              onChange={(e) => setEditForm({ ...editForm, requer_responsavel: e.target.checked })} />
                            Responsável
                          </label>
                          <button onClick={handleSaveEdit} data-testid={`save-edit-${cat.id}`}
                            className="p-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 rounded">
                            <Save size={14} />
                          </button>
                          <button onClick={() => setEditCat(null)}
                            className="p-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded">
                            <X size={14} />
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div key={cat.id} className={`flex items-center gap-3 px-5 py-2.5 border-b border-gray-800/50 last:border-0 ${!cat.ativo ? 'opacity-50' : ''}`}>
                        <List size={14} className="text-gray-600" />
                        <span className="text-sm text-gray-300 flex-1">{cat.nome}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${cat.tipo === 'entrada' ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' : 'text-red-400 bg-red-500/10 border-red-500/30'}`}>
                          {cat.tipo === 'entrada' ? 'Entrada' : 'Saída'}
                        </span>
                        {cat.sistema ? (
                          <span className="text-xs text-gray-600 flex items-center gap-1"><Lock size={12} />Sistema</span>
                        ) : (
                          <span className="text-xs text-yellow-500 flex items-center gap-1"><Unlock size={12} />Personalizada</span>
                        )}
                        {cat.requer_destino && <span className="text-xs text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded">Destino</span>}
                        {cat.requer_responsavel && <span className="text-xs text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded">Responsável</span>}
                        <button onClick={() => handleToggle(cat.id)} data-testid={`toggle-cat-${cat.id}`}
                          title={cat.ativo ? 'Desativar (inativar)' : 'Reativar'}
                          className="text-gray-500 hover:text-white transition-colors">
                          {cat.ativo ? <Unlock size={14} className="text-emerald-400" /> : <Lock size={14} className="text-red-400" />}
                        </button>
                        {canEdit && (!cat.sistema || isMaster) && (
                          <>
                            <button onClick={() => openEdit(cat)} data-testid={`edit-cat-${cat.id}`}
                              title="Editar categoria"
                              className="p-1 text-gray-500 hover:text-yellow-400 hover:bg-yellow-500/10 rounded transition-colors">
                              <Pencil size={13} />
                            </button>
                            <button onClick={() => handleDelete(cat)} data-testid={`delete-cat-${cat.id}`}
                              title="Excluir categoria (auditado)"
                              className="p-1 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors">
                              <Trash2 size={13} />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
